import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getI18nLanguageCode, getUrlLanguageCode, supportedLanguages } from '../utils/i18n-routes';

interface LanguageRouteProps {
  children: React.ReactNode;
}

/**
 * Component that handles language detection from URL and syncs with i18n
 */
const LanguageRoute: React.FC<LanguageRouteProps> = ({ children }) => {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // If lang param exists, use it to set language
    if (lang) {
      const i18nLang = getI18nLanguageCode(lang);
      
      // Only change language if it's different from current
      if (i18nLang !== i18n.language && supportedLanguages.includes(i18nLang)) {
        i18n.changeLanguage(i18nLang);
      }
    } else {
      // No lang param - check if we should redirect to language-prefixed URL
      const currentLang = i18n.language || 'en';
      const urlLang = getUrlLanguageCode(currentLang);
      
      // If not English, redirect to language-prefixed URL (for SEO)
      // Skip for admin routes and auth routes
      const path = location.pathname;
      const isAdminRoute = path.startsWith('/admin');
      const isAuthRoute = path.startsWith('/auth');
      
      if (urlLang !== 'en' && !isAdminRoute && !isAuthRoute && path !== '/') {
        // Preserve query parameters (e.g., ?token=abc123)
        const searchParams = location.search;
        const newPath = `/${urlLang}${path}${searchParams}`;
        navigate(newPath, { replace: true });
      }
    }
  }, [lang, i18n, navigate, location.pathname, location.search]);

  return <>{children}</>;
};

export default LanguageRoute;

